import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, Camera, Car, CheckCircle2, Clock, Plus, RefreshCw, Trash2, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useFacility } from '../context/FacilityContext';
import camerasService from '../services/camerasService';
import AddOnPrompt from '../components/AddOnPrompt';

/**
 * AI Camera Agent — the camera intelligence site surface for ONE facility.
 *
 * Camera intelligence is a MICROSERVICE the product connects to, per
 * facility (2026-08-29, same decision as the marketing engine): the
 * product contains no vision logic and is provider-neutral — a facility
 * connects the engine of its choice. This page renders that engine's read
 * views through the server-side passthrough, in three states:
 *
 *   connect  — pointer to Settings → Third-party apps → Camera intelligence
 *   setup    — the engine's requirement checklist + the camera registry
 *              (a camera is one of the requirements)
 *   ready    — the Morning Brief, the open-review list with OK / Not OK
 *              (the entire training interface), cameras, known vehicles
 *
 * Isolation rule: only this facility's site, never siblings.
 * Failure is honest: unreachable → named outage + Retry, never stale numbers.
 */

const SEVERITY = {
  high: 'bg-rose-100 text-rose-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-slate-100 text-slate-600',
  info: 'bg-slate-100 text-slate-500',
};

const Reasons = ({ reasons }) => (
  <ul className="mt-1 space-y-0.5">
    {(reasons || []).map((r, i) => (
      <li key={`${r.code}-${i}`} className="text-[11px] text-slate-500 flex items-start gap-1">
        <span className="font-mono text-[10px] px-1 rounded bg-slate-100 text-slate-500 mt-px">{r.code}</span>
        <span>{r.detail}</span>
      </li>
    ))}
  </ul>
);

const EventRow = ({ event, onVerdict, busy }) => (
  <div className="px-5 py-3" data-testid={`camera-event-${event.event_id}`}>
    <div className="flex items-center gap-2 mb-1 flex-wrap">
      <Clock className="h-3 w-3 text-slate-400" />
      <span className="text-[11px] text-slate-400">
        {String(event.occurred_at_local || event.occurred_at || '').slice(0, 16).replace('T', ' ')}
      </span>
      {event.camera && <span className="text-[11px] text-slate-500">{event.camera}</span>}
      {event.zone && <span className="text-[11px] text-slate-400">· {event.zone}</span>}
      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${SEVERITY[event.severity] || SEVERITY.info}`}>
        {event.severity}
      </span>
      {event.verdict && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-medium">
          marked {event.verdict === 'ok' ? 'OK' : 'Not OK'}
        </span>
      )}
    </div>
    <p className="text-[12px] text-slate-700">
      {event.object_class}{event.plate ? ` · plate ${event.plate}` : ''}
      {event.vehicle_count != null ? ` · ${event.vehicle_count} vehicle${event.vehicle_count === 1 ? '' : 's'}` : ''}
      {event.description ? ` — ${event.description}` : ''}
    </p>
    <Reasons reasons={event.reasons} />
    <div className="mt-2 flex items-center gap-2 flex-wrap">
      {event.clip_url && (
        <a href={event.clip_url} target="_blank" rel="noreferrer"
          className="text-[12px] text-blue-600 underline hover:no-underline">View clip</a>
      )}
      {!event.verdict && onVerdict && (
        <>
          <button type="button" disabled={busy} onClick={() => onVerdict(event, 'ok')}
            data-testid={`camera-ok-${event.event_id}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-[12px] font-medium hover:bg-blue-700 disabled:opacity-50">
            <CheckCircle2 className="h-3.5 w-3.5" /> OK
          </button>
          <button type="button" disabled={busy} onClick={() => onVerdict(event, 'not_ok')}
            data-testid={`camera-notok-${event.event_id}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-rose-600 text-white text-[12px] font-medium hover:bg-rose-700 disabled:opacity-50">
            <XCircle className="h-3.5 w-3.5" /> Not OK
          </button>
        </>
      )}
    </div>
  </div>
);

const Cameras = () => {
  const { facilityId, loading: facilityLoading } = useFacility();
  const [panel, setPanel] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [actors, setActors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', stream_url: '', zone: '', gate: '' });

  const load = useCallback(async () => {
    if (!facilityId) {
      if (!facilityLoading) setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await camerasService.panel(facilityId);
      const p = res.data || {};
      setPanel(p);
      if (p.state === 'setup' || p.state === 'ready') {
        const [c, a] = await Promise.all([
          camerasService.listCameras(facilityId).catch(() => ({ data: [] })),
          camerasService.listActors(facilityId).catch(() => ({ data: [] })),
        ]);
        setCameras(Array.isArray(c.data) ? c.data : []);
        setActors(Array.isArray(a.data) ? a.data : []);
      }
    } catch (err) {
      setPanel({ state: 'unreachable' });
    } finally {
      setLoading(false);
    }
  }, [facilityId, facilityLoading]);

  useEffect(() => { load(); }, [load]);

  const verdict = async (event, value) => {
    setBusy(true);
    try {
      const res = await camerasService.feedback(facilityId, event.event_id, value);
      const learned = res.data?.learned || {};
      if (value === 'ok' && learned.rule_id) {
        toast.success('Marked OK — this pattern won’t be flagged again.');
      } else if (value === 'ok' && learned.plates) {
        toast.success(`Marked OK — learned plate ${learned.plates.join(', ')}.`);
      } else {
        toast.success(value === 'ok' ? 'Marked OK.' : 'Marked Not OK — similar events will rank higher.');
      }
      await load();
    } catch {
      toast.error('Could not record that — the engine did not respond.');
    } finally {
      setBusy(false);
    }
  };

  const createCamera = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await camerasService.createCamera(facilityId, form);
      setForm({ name: '', stream_url: '', zone: '', gate: '' });
      setShowForm(false);
      toast.success('Camera added.');
      await load();
    } catch {
      toast.error('Could not add the camera.');
    } finally {
      setBusy(false);
    }
  };

  const deleteCamera = async (cam) => {
    if (!window.confirm(`Remove ${cam.name}?`)) return;
    try {
      await camerasService.deleteCamera(facilityId, cam.camera_id);
      await load();
    } catch {
      toast.error('Could not remove the camera.');
    }
  };

  const runVerify = async () => {
    setBusy(true);
    try {
      const res = await camerasService.verify(facilityId);
      toast.success(res.data?.ready ? 'Site is ready — the first brief lands at 6 a.m.' : 'Verification run — see what is still open.');
      await load();
    } catch {
      toast.error('Verification failed — the engine did not respond.');
    } finally {
      setBusy(false);
    }
  };

  if (!facilityId && !facilityLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <h2 className="text-lg font-semibold text-slate-900">No facility selected</h2>
        <p className="text-[13px] text-slate-500 mt-1">Create a facility in Settings to connect cameras.</p>
      </div>
    );
  }

  if (loading || facilityLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const state = panel?.state || 'unreachable';

  const cameraAddOn = (testId) => (
    <AddOnPrompt
      icon={Camera} name="Camera intelligence" appKey="camera" testId={testId}
      summary="Watches this facility’s gate activity and cameras, learns what is normal here, and tells you what needs a look."
      points={[
        'A Morning Brief every day',
        'Unusual activity flagged for a quick OK / Not OK',
        'Known vehicles recognised at the gate',
      ]}
    />
  );

  if (!panel?.engine_configured && state !== 'unreachable') {
    return cameraAddOn('camera-state-unconfigured');
  }

  if (state === 'unreachable') {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-5" data-testid="camera-state-unreachable">
        <div className="flex items-center gap-2 text-[14px] font-semibold text-amber-900">
          <AlertTriangle className="h-4 w-4" /> Camera engine unreachable
        </div>
        <p className="text-[13px] text-amber-800 mt-1">
          The engine didn’t answer, so nothing is shown rather than something stale.
        </p>
        <button type="button" onClick={load}
          className="mt-3 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700">
          <RefreshCw className="h-3.5 w-3.5" /> Retry
        </button>
      </div>
    );
  }

  if (state === 'connect' && panel.error !== 'key_rejected') {
    return cameraAddOn('camera-state-connect');
  }

  if (state === 'connect') {
    return (
      <div className="rounded-xl border border-slate-200/80 bg-white p-6" data-testid="camera-state-connect">
        <div className="flex items-center gap-2 mb-1">
          <Camera className="h-4 w-4 text-slate-500" />
          <h2 className="text-[15px] font-semibold text-slate-900">Connect this facility’s cameras</h2>
        </div>
        <p className="text-[13px] text-rose-700 mb-3">
          The engine rejected this facility’s stored key. Paste a current key to reconnect.
        </p>
        <Link to="/settings?tab=apps&app=camera"
          className="inline-flex items-center px-3.5 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700">
          Connect in Third-party apps
        </Link>
      </div>
    );
  }

  const brief = panel.brief || null;
  const openEvents = panel.open_events || [];
  const requirements = panel.requirements || [];

  const camerasCard = (
    <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden" data-testid="camera-cameras">
      <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
        <h2 className="text-[13px] font-semibold text-slate-700">Cameras</h2>
        <button type="button" onClick={() => setShowForm(!showForm)}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-[12px] font-medium hover:bg-blue-700">
          <Plus className="h-3.5 w-3.5" /> Add camera
        </button>
      </div>
      {showForm && (
        <form onSubmit={createCamera} className="px-5 py-4 border-b border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input type="text" required placeholder="Camera name (e.g. Gate lane)" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="rounded-lg border border-slate-200 px-3 py-2 text-[13px] sm:col-span-2" />
          <input type="text" placeholder="Stream URL for the on-site bridge (rtsp://…)" value={form.stream_url}
            onChange={(e) => setForm({ ...form, stream_url: e.target.value })}
            className="rounded-lg border border-slate-200 px-3 py-2 text-[13px] sm:col-span-2" />
          <input type="text" placeholder="Zone (e.g. row C)" value={form.zone}
            onChange={(e) => setForm({ ...form, zone: e.target.value })}
            className="rounded-lg border border-slate-200 px-3 py-2 text-[13px]" />
          <input type="text" placeholder="Watches gate (name from the gate log, e.g. main)" value={form.gate}
            onChange={(e) => setForm({ ...form, gate: e.target.value })}
            className="rounded-lg border border-slate-200 px-3 py-2 text-[13px]" />
          <div className="sm:col-span-2 flex items-center gap-2">
            <button type="submit" disabled={busy}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-[12px] font-medium hover:bg-blue-700 disabled:opacity-50">
              Add
            </button>
            <button type="button" onClick={() => setShowForm(false)}
              className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 text-[12px] font-medium hover:bg-slate-200">
              Cancel
            </button>
          </div>
        </form>
      )}
      <div className="divide-y divide-slate-100">
        {cameras.map((cam) => (
          <div key={cam.camera_id} className="px-5 py-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
              <Camera className="h-4 w-4 text-slate-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium text-slate-800 truncate">{cam.name}</p>
              <p className="text-[11px] text-slate-400 truncate">
                {cam.zone || 'no zone'}{cam.gate ? ` · watches gate ${cam.gate}` : ''}
              </p>
            </div>
            <button type="button" onClick={() => deleteCamera(cam)} title="Remove camera"
              className="p-1.5 rounded-md text-slate-300 hover:text-red-500">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        {cameras.length === 0 && (
          <p className="text-center text-[13px] text-slate-400 py-8">
            No cameras registered yet. Add the ones the on-site bridge should watch.
          </p>
        )}
      </div>
    </div>
  );

  if (state === 'setup') {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="camera-state-setup">
        <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-[13px] font-semibold text-slate-700">Set up {panel.tenant_name || 'this site'}</h2>
            <button type="button" onClick={runVerify} disabled={busy}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-[12px] font-medium hover:bg-blue-700 disabled:opacity-50">
              <RefreshCw className="h-3.5 w-3.5" /> Run verification
            </button>
          </div>
          <div className="divide-y divide-slate-100">
            {requirements.map((r) => (
              <div key={r.key} className="px-5 py-3">
                <div className="flex items-center gap-2">
                  {r.status === 'pass' ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                    : r.status === 'fail' ? <XCircle className="h-3.5 w-3.5 text-rose-600" />
                      : <Clock className="h-3.5 w-3.5 text-slate-400" />}
                  <span className="text-[13px] font-medium text-slate-800 font-mono">{r.key}</span>
                  {r.required ? (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">required</span>
                  ) : (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-50 text-slate-400">recommended</span>
                  )}
                </div>
                <p className="text-[12px] text-slate-500 mt-1">{r.needed_for}</p>
                {r.how && <p className="text-[11px] text-slate-400 mt-0.5">{r.how}</p>}
              </div>
            ))}
            {requirements.length === 0 && (
              <p className="text-center text-[13px] text-slate-400 py-8">
                Run verification to see what the engine still needs.
              </p>
            )}
          </div>
        </div>
        {camerasCard}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="camera-state-ready">
      <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden" data-testid="camera-brief">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 className="text-[13px] font-semibold text-slate-700">
              Morning Brief{brief?.date ? ` — ${brief.date}` : ''}
            </h2>
            <p className="text-[11px] text-slate-400">{panel.tenant_name}</p>
          </div>
          <button type="button" onClick={load}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 text-[12px] font-medium hover:bg-slate-200">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>
        <div className="px-5 py-4">
          <p className={`text-[15px] font-semibold ${brief?.all_clear ? 'text-emerald-700' : 'text-slate-900'}`}>
            {brief?.headline || 'No brief yet'}
          </p>
          {brief?.totals && (
            <p className="text-[12px] text-slate-500 mt-1">
              The engine ignored {brief.totals.ignored ?? 0} of {brief.totals.events ?? 0} events
              {brief.totals.suppressed_by_rules ? ` (${brief.totals.suppressed_by_rules} by rules you taught it)` : ''}
              {brief.totals.access != null ? ` · ${brief.totals.access} gate events` : ''}
              {brief.ignored_by_class && Object.keys(brief.ignored_by_class).length > 0
                ? ` · ignored: ${Object.entries(brief.ignored_by_class).map(([k, v]) => `${v} ${k}`).join(', ')}`
                : ''}
            </p>
          )}
          {brief?.next_brief_local && (
            <p className="text-[11px] text-slate-400 mt-1">
              Next brief {String(brief.next_brief_local).slice(0, 16).replace('T', ' ')}
            </p>
          )}
        </div>
        {(brief?.noteworthy || []).length > 0 && (
          <div className="divide-y divide-slate-100 border-t border-slate-100">
            {brief.noteworthy.map((ev) => (
              <EventRow key={ev.event_id} event={ev} onVerdict={verdict} busy={busy} />
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden" data-testid="camera-review">
          <div className="px-5 py-3.5 border-b border-slate-100">
            <h2 className="text-[13px] font-semibold text-slate-700">
              To review{openEvents.length ? ` (${openEvents.length})` : ''}
            </h2>
          </div>
          <div className="divide-y divide-slate-100 max-h-[32rem] overflow-y-auto">
            {openEvents.map((ev) => (
              <EventRow key={ev.event_id} event={ev} onVerdict={verdict} busy={busy} />
            ))}
            {openEvents.length === 0 && (
              <p className="text-center text-[13px] text-slate-400 py-8">
                Nothing waiting for a verdict.
              </p>
            )}
          </div>
        </div>

        <div className="space-y-4">
          {camerasCard}
          <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden" data-testid="camera-actors">
            <div className="px-5 py-3.5 border-b border-slate-100">
              <h2 className="text-[13px] font-semibold text-slate-700">Known vehicles</h2>
            </div>
            <div className="divide-y divide-slate-100">
              {actors.map((a) => (
                <div key={a.actor_id} className="px-5 py-3 flex items-center gap-3">
                  <Car className="h-4 w-4 text-slate-400" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] text-slate-800 truncate">{a.label || a.kind}</p>
                    <p className="text-[11px] text-slate-400 truncate">
                      {(a.plates || []).join(', ') || 'no plate'}
                      {a.sightings ? ` · ${a.sightings} sighting${a.sightings === 1 ? '' : 's'}` : ''}
                    </p>
                  </div>
                </div>
              ))}
              {actors.length === 0 && (
                <p className="text-center text-[13px] text-slate-400 py-6">
                  Nothing learned yet — marking a gate event OK teaches the vehicle.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cameras;
